// Authenticated CSV download for the Products module (list = product-summary,
// detail = per-product movements). Duplicated from features/reports/csv.ts's
// `useCsvDownload` verbatim rather than imported — that file's own header
// explains why: a plain `<a href>` can't carry the `Authorization`/
// `X-Tenant-Id` headers the API requires, and `src/api/client.ts`'s `apiFetch`
// only parses JSON bodies, so a `text/csv` response needs its own fetch path.
// Kept as a small, self-contained sibling per feature (same convention this
// module's api.ts/format.ts already follow) rather than editing api/client.ts
// (out of this goal's ownership) or importing across the reports/products
// feature boundary.
import { useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';

/** Mirrors src/api/client.ts's DEFAULT_BASE_URL exactly (services/api's local
 * dev server default port — services/api/src/index.ts). */
const DEFAULT_BASE_URL = 'http://localhost:3001/api/v1';

function baseUrl(): string {
  return import.meta.env.VITE_API_URL ?? DEFAULT_BASE_URL;
}

export type CsvQuery = Record<string, string | number | boolean | undefined>;

function buildCsvUrl(path: string, query: CsvQuery): string {
  const url = new URL(path.replace(/^\//, ''), `${baseUrl().replace(/\/$/, '')}/`);
  url.searchParams.set('format', 'csv');
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function filenameFromDisposition(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return match?.[1] ?? fallback;
}

/**
 * Returns a `download(path, query, fallbackFilename)` function that fetches a
 * report's CSV variant as a Blob (with the same bearer/tenant headers every
 * other request carries) and triggers a normal browser file-save.
 */
export function useCsvDownload() {
  const auth = useAuth();

  return useCallback(
    async (path: string, query: CsvQuery, fallbackFilename: string): Promise<void> => {
      const headers: Record<string, string> = { Accept: 'text/csv' };
      if (auth.token) headers.Authorization = `Bearer ${auth.token}`;
      if (auth.activeTenant) headers['X-Tenant-Id'] = auth.activeTenant.tenantId;

      const response = await fetch(buildCsvUrl(path, query), { headers });
      if (!response.ok) {
        throw new Error(`CSV download failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const filename = filenameFromDisposition(response.headers.get('content-disposition'), fallbackFilename);

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    },
    [auth.token, auth.activeTenant],
  );
}
